import { Component, forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ThemeProvider, Toast } from '@gupshup-frontend/gupshup-ts-components';
import { TemplateMessaging } from '@gs/gs-integration-component';
import { getTemplates, uploadMedia } from '../../api/gsIntg';
import { buildDefinition } from '../../webhook/buildDefinition';
import { messageTypeForTemplate } from '../../webhook/messageType';
import { hasTemplateDrifted } from '../../webhook/templateDrift';
import { isSameDestination, normalizeDestination } from '../../webhook/destination';
import { rewriteFieldDoubleBraces } from '../../webhook/doubleBraces';
import { toWidgetState, byteLength, fromWidgetState, MAX_BYTES } from '../../state/widgetState';
import { mapTemplate } from '../../helper/utils';
import { digitsOnly, enterpriseChannels } from '../../helper/channels';
import { APP_NAME, GUPSHUP_THEME_CONFIG, ERROR_COPY, LOCAL_FORM_ALL_TEMPLATES } from '../../utils/constants';
import {
  LOCAL_SEND, resolveLocalSendPanel, choosePrefill, draftHasValue, draftHasDoubleBrace, isTypeTemplate, dynamicButtonValue,
  DYNAMIC_BUTTON_FIELDS, libraryVariableCount,
} from '../../templateSend';
import SendToControl from './SendToControl';
import ConsentControl from './ConsentControl';
import TemplateSwitcher from './TemplateSwitcher';
import { draftFromSubmitData, sameValues } from './draftFromSubmitData';
import { CUSTOMISATIONS_BASE } from './customisations';

const FOOTER_HELPER_TEXT = 'Save here, then use Test webhook or Done.';

const sameId = (a, b) => String(a) === String(b);

// A channel's raw template list as it goes on screen: the ENABLED templates our forms can send, and how many of each kind
// were left out. A kind is left out only while its LOCAL_SEND flag is off (the same flags resolveLocalSendPanel reads), so
// flipping a flag back hides it again. A cardless carousel can't be sent at all and stays out whatever the flag, with no
// note (user decision 2026-09-29).
const buildChannelList = (raw) => {
  const enabled = (Array.isArray(raw) ? raw : []).filter((template) => template.status === 'ENABLED');
  const isCarousel = (template) => template.button_type === 'CAROUSEL';
  const hasCards = (template) => Array.isArray(template.carouselCards) && template.carouselCards.length > 0;
  const isLocation = (template) => template.type === 'LOCATION';
  const isGif = (template) => template.type === 'GIF';
  const countLeftOut = (sendable, isKind) => (sendable ? 0 : enabled.filter(isKind).length);
  return {
    mapped: enabled
      .filter((template) => (LOCAL_SEND.location || !isLocation(template))
        && (LOCAL_SEND.gif || !isGif(template))
        && (!isCarousel(template) || (LOCAL_SEND.carousel && hasCards(template))))
      .map((template, index) => mapTemplate(template, index)),
    hidden: {
      location: countLeftOut(LOCAL_SEND.location, isLocation),
      carousel: countLeftOut(LOCAL_SEND.carousel, isCarousel),
      gif: countLeftOut(LOCAL_SEND.gif, isGif),
    },
  };
};

// The counts are always kept per kind; this only decides how they're worded. Flag off collapses all three into one
// combined sentence, since that's the original (pre-carousel-support) copy. Flag on, cardless carousels are left out of
// the list with no note (their count stays 0).
const hiddenNoteText = ({ location, carousel, gif }) => {
  if (LOCAL_SEND.carousel) {
    const parts = [];
    if (location > 0) {
      parts.push(`${location} template${location === 1 ? '' : 's'} hidden: location templates are not supported yet.`);
    }
    if (gif > 0) {
      parts.push(`${gif} GIF template${gif === 1 ? '' : 's'} hidden: they can't be sent from here yet.`);
    }
    return parts.join(' ');
  }
  const total = location + carousel + gif;
  return total > 0 ? `${total} template${total === 1 ? '' : 's'} hidden: location, carousel and GIF templates are not supported yet.` : '';
};

// The shared component reads defaultTemplate.variables by position, header slots first. A short list makes
// it write "undefined" into the message (TemplateMessaging.tsx:139/143), so an empty or missing slot gets the
// component's own placeholder, {{p}} with p counted across header then body (:161-163). It renders empty (:355)
// and the Footer keeps Save blocked until it is filled. A restore is padded too when its saved list is shorter
// than the template (the template gained a variable since Save): that slot shows empty and Save stays blocked
// until it is filled, where it used to write "undefined".
const padSlots = (values, count, offset) => {
  const slots = [...(values || [])];
  for (let i = 0; i < count; i += 1) {
    if (!slots[i]) slots[i] = `{{${offset + i + 1}}}`;
  }
  return slots;
};

// The shared component reopens a template with its values through defaultTemplate. Used to restore a
// saved node, to keep the marketer on the form after Save (fromSave hides the restore banner), and to open
// a template with carried values.
const toRestoreView = (saved, template, channel, key, fromSave = false) => {
  // Slot counts are the library's own (variableCount), which can differ from describeTemplate's.
  const headerCount = libraryVariableCount(template.details?.header);
  return {
    defaultChannel: channel,
    defaultTemplate: {
      template,
      // waTemplateJson appended last (never in the first variablesCount slots the library's own restore
      // hook reads positionally) so its restore-hook effect (TemplateMessaging.tsx:129-160) can pick it
      // out via its own `typeof === 'object'` check and re-seed setButtonParamsObj/setCarouselDetails.
      variables: [
        ...padSlots(saved.headerParams, headerCount, 0),
        ...padSlots(saved.params, libraryVariableCount(template.details?.body), headerCount),
        ...(saved.waTemplateJson ? [saved.waTemplateJson] : []),
      ],
      media: saved.mediaUrl,
      dynamicButton: dynamicButtonValue(saved),
    },
    key,
    fromSave,
  };
};

// Every "our panel or the shared component" decision goes through here, so the own-form flag reaches each one.
const resolvePanel = (template) => resolveLocalSendPanel(template, { allTemplates: LOCAL_FORM_ALL_TEMPLATES });

// Whether we can see the values typed on a template: our panels report theirs, and with the own-form flag on we also
// record what we opened the shared component's forms with. Off, the shared component's templates keep carrying from the
// last Save, as they always did. Read where we record a library open and where we consult the session store.
const tracksValues = (template) => LOCAL_FORM_ALL_TEMPLATES || !!resolvePanel(template);

const memoryKey = (channelId, templateId) => `${channelId}:${templateId}`;

// A session entry is a draft without the message's own fields, which the saved draft owns.
const toEntry = ({ destination, consentCategory, isTemplate, ...entry }) => entry;

// The shared component's own Back button on its form view. Scoped to the library's wrapper, which a panel is never in.
const LIBRARY_BACK_BUTTON = '.br-library .nic-tm-footer-container .nic-tm-inside-main-container button';

// Skips the render while hidden, so the shared component kept behind a panel is not redrawn by every host render (its
// table draws every row). The render that hides it still applies, and so does the one that shows it again.
const WhileHidden = memo(function WhileHidden({ children }) {
  return children;
}, (prev, next) => prev.hidden && next.hidden);

// One function that always runs the latest closure, with an identity that never changes: a memoized child keeps it as a
// prop while the handler reads current state.
const useStableCallback = (fn) => {
  const latest = useRef(fn);
  latest.current = fn;
  return useCallback((...args) => latest.current(...args), []);
};

// Catches TemplateMessaging's restore-throw so a bad restore can't crash the app.
class TemplateMessagingBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    // Swallowed on purpose — see class comment.
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// A saved message keeps its own category, even an empty one (general consent, on purpose); a new message starts
// from the org's.
const startingConsent = (saved, org) => String(saved ?? org?.config?.consentCategory ?? '');

const Message = forwardRef(({ orgId, org, launchParams = {}, phoneAttribute, reloadOrg, incomingWidgetState, initialState, onStateChange, onError }, ref) => {
  // What the own-form flag switches, named once. Read per render, not at import: the tests flip it.
  const KEEP_LIBRARY = LOCAL_FORM_ALL_TEMPLATES; // the shared component stays mounted, hidden, behind a panel
  const SHOW_SWITCHER = LOCAL_FORM_ALL_TEMPLATES; // the template name is a dropdown
  const SHOW_RESET_TYPED = LOCAL_FORM_ALL_TEMPLATES; // the template list offers to forget the unsaved values

  const [templates, setTemplates] = useState([]);
  // The local panel on screen: { key, Panel, channelId, template, restored, instanceKey, restoredFromNode }. restored is
  // what it opens with (null: bare), read on mount only, so instanceKey remounts it. restoredFromNode is true for the
  // panel a restore opened, until its first Save or a Dismiss: it feeds the restore banner.
  const [localSelection, setLocalSelection] = useState(null);
  // Numbers the plain instance of the shared component, so a new number remounts it (Back from a panel when its list
  // cannot be kept, see handleLocalBack).
  const [libraryEpoch, setLibraryEpoch] = useState(0);
  const [hiddenKinds, setHiddenKinds] = useState({ location: 0, carousel: 0, gif: 0 });
  const [templatesLoadedForChannel, setTemplatesLoadedForChannel] = useState(null);
  // Lazy init from initialState — getState() is correct right after a tab remount.
  const [computedState, setComputedState] = useState(() => initialState || null);
  // The message's destination: one value per message, so it survives picking a different template.
  // Takes effect on Save, like every other field. initialState is Widget's latestState — { webhook,
  // widget_state } with no draft — so a remount reads it from widget_state.
  const [destination, setDestination] = useState(() => normalizeDestination(initialState?.widget_state?.destination, phoneAttribute));
  // The message's consent category, per message like the destination: one org serves many Bloomreach projects, and
  // each names its own categories. Empty sends general consent. Save commits everything, so a change takes effect with
  // the next Save, like the destination above.
  const [consentCategory, setConsentCategory] = useState(() => startingConsent(initialState?.widget_state?.consentCategory, org));
  // Mirrors the builder's own selectedTemplate callback (shape: mapTemplate()) — also set directly
  // for a local-panel pick/restore, since a local panel replaces the library entirely and never
  // fires that callback itself.
  const [selectedTemplateInfo, setSelectedTemplateInfo] = useState(null);
  // { defaultChannel, defaultTemplate, key, fromSave }. fromSave hides the restore banner: a Save sets it, and so does Dismiss.
  const [restoreView, setRestoreView] = useState(null);
  // { fromName, kept } while the open form holds values carried from the saved message of another
  // template; it feeds the notice, and Save, Back and Start blank end it.
  const [carry, setCarry] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [hasDoubleBrace, setHasDoubleBrace] = useState(false);
  // Set by openTemplate when the values it carries in still hold a double brace; the effect after the watch reads it once
  // the form is on screen.
  const braceOnOpenRef = useRef(false);
  // Whether the open panel's last report differs from the saved message. Set only when it flips: React skips the render for
  // an unchanged boolean, so a keystroke re-renders the panel and not this component.
  const [valuesDirty, setValuesDirty] = useState(false);
  // The saved message's values as a panel reports them: what "unchanged" is measured against. A node the shared component
  // saved holds wire JSON in the library's format (uppercase sub_type, another key order, null where a panel writes ''),
  // which no panel regenerates as it was, so the stored draft's text can't be compared with a report. This is the first
  // report of a panel opened with the saved values (a restore, or a reopen with nothing changed). Null while unknown: before
  // such a panel has reported, and after any Save, which makes the stored draft the payload just saved. The stored draft
  // is compared then.
  const savedReportRef = useRef(null);
  // instanceKey of the last panel to report; any other key is a panel's first report.
  const reportedInstanceRef = useRef(null);
  // { instanceKey, draft }: the values the open panel reported last. The saved message can change under a panel already open
  // (a restore that lands after a pick), and the status is measured again against it. Null after a Save, which resets it.
  const lastReportRef = useRef(null);
  const restoreAttemptedRef = useRef(false);
  // Counts Saves: a restore still loading when one lands is no longer the saved message.
  const savesRef = useRef(0);
  // False once the tab has gone: an answer that lands then has nobody to show it to.
  const mountedRef = useRef(true);
  // Numbers every view we open (post-save reopen, carry-over, bare open, local panel), so each gets its own key and
  // remounts on its values instead of updating in place.
  const saveCountRef = useRef(0);
  // Id of the template a view we opened is on. The shared component reports its template again when it mounts, and that
  // is not a new pick. Cleared on Back, so picking the same template again is a pick.
  const openedOnRef = useRef(null);
  // The channel asked for last (an ask of the shared component, or a restore that put its channel on screen). Only its
  // reply may set the list on screen or reach the shared component, whose filter shows that channel: a slower reply for
  // another one would put that channel's rows under it.
  const newestChannelRef = useRef(null);
  // Each channel's list for the life of this mount. A remount of the shared component asks again, and this answers.
  const loadedListsRef = useRef(new Map());
  // Set by the first pick. A restore still loading by then must not open its template over it.
  const pickedRef = useRef(false);
  // The latest values of every template this session, saved or not, by memoryKey: what a pick or a switch reopens a
  // template with, so a visit to one that cannot take the values loses nothing. It never feeds saveDraft, computeState or
  // the webhook. A ref, so a keystroke re-renders only the panel.
  const sessionRef = useRef(new Map());
  // Key of the latest entry that holds a value: the working draft, which is what a pick carries from. An entry that holds
  // nothing (a panel opened and left empty, or emptied after it held values) is not it, or one empty stop would shadow the
  // saved message and every value typed before it. A template opened bare on the shared component is the exception, as
  // noteLibraryOpen says.
  const lastValuedKeyRef = useRef(null);
  // The wrapper of what the marketer composes in: the shared component and our panel, nothing else.
  const composerRef = useRef(null);
  // The root of the tab: where focus goes when the button that held it goes away (keepFocus).
  const messageRef = useRef(null);

  // org only changes reference when WidgetShell actually re-fetches it (loadOrg) — memoized so
  // unrelated re-renders here (toast, restore banner, double-brace scan, etc.) don't rebuild this.
  const channels = useMemo(() => enterpriseChannels(org), [org]);
  // The channel the template list on screen belongs to. Kept as a Channel (not just its id) for
  // TemplateMessaging's defaultChannel prop — needed so returning from a carousel (Back, or after Save)
  // reopens the list on whatever channel was active, instead of resetting to channels[0].
  const activeChannelObj = channels.find((channel) => channel.value === templatesLoadedForChannel);
  // The channel the saved node is on, when the org still has it. The plain view opens on it while no list has loaded, so
  // the ask the shared component makes as it mounts is for the channel the restore loads, not for channels[0].
  const savedChannelId = useMemo(() => fromWidgetState(incomingWidgetState)?.channelId, [incomingWidgetState]);
  const savedChannelObj = savedChannelId ? channels.find((channel) => channel.value === savedChannelId) : undefined;
  // The channel of the form on screen: a local panel is on its own channel, else it is the list's. They differ only while a
  // list that is not the panel's is on screen. The "via" label, the consent number, the save status, a pick and Start blank
  // go by this one.
  const openChannelId = localSelection?.channelId ?? templatesLoadedForChannel;
  const openChannelObj = channels.find((channel) => channel.value === openChannelId);

  const findTemplate = (id, list = templates) => list.find((t) => sameId(t.details.id, id));

  // The list a draft's template is found in: its own channel's, not whichever list is on screen. `fallback` is for a channel that
  // never loaded.
  const listOf = (draft, fallback = []) => loadedListsRef.current.get(draft.channelId)?.mapped ?? fallback;

  // Puts a channel's list on screen: the one place these three are written, for a reply and for a restore alike.
  const applyList = (channelId, loaded) => {
    setTemplates(loaded.mapped);
    setHiddenKinds(loaded.hidden);
    setTemplatesLoadedForChannel(channelId);
  };

  // A channel's list, from this mount's cache or from the server. Puts nothing on screen.
  const fetchChannelList = async (channelId) => {
    let loaded = loadedListsRef.current.get(channelId);
    if (!loaded) {
      loaded = buildChannelList(await getTemplates(orgId, channelId));
      loadedListsRef.current.set(channelId, loaded);
    }
    return loaded;
  };

  // The shared component's ask, from its Channels filter or as it mounts. Asks overlap (each click sends one, and a restore
  // is loading beside them), and the component sets its rows from every reply it awaits. Each ask still fetches, but only
  // the newest channel's reply goes on screen and back to the component. An older one gets the newest channel's list
  // instead, or none while that is still loading, so the rows never belong to another channel than the filter shows.
  const loadTemplatesForChannel = async (channelId) => {
    newestChannelRef.current = channelId;
    const newestList = () => loadedListsRef.current.get(newestChannelRef.current)?.mapped ?? [];
    let loaded;
    try {
      loaded = await fetchChannelList(channelId);
    } catch (error) {
      // An ask nobody waits for any more is not worth a report either.
      if (newestChannelRef.current === channelId) throw error;
      return newestList();
    }
    if (newestChannelRef.current !== channelId) return newestList();
    applyList(channelId, loaded);
    return loaded.mapped;
  };

  // The one writer of the session store; the working draft is the latest entry written with a value in it. Start blank's
  // mark (`startedBlank`) outlasts the blank forms reported after it: only a value replaces it.
  const remember = (draft) => {
    const entry = toEntry(draft);
    const key = memoryKey(entry.channelId, entry.templateId);
    const hasValue = draftHasValue(entry);
    if (!hasValue && sessionRef.current.get(key)?.startedBlank) return;
    sessionRef.current.set(key, entry);
    if (hasValue) {
      lastValuedKeyRef.current = key;
    } else if (lastValuedKeyRef.current === key) {
      // It held values and was emptied: a blank entry must not shadow the saved message, as one that never held any does not.
      lastValuedKeyRef.current = null;
    }
  };

  // Every open is a new instance (new key), so the panel reads `restored` again from scratch.
  const showLocalPanel = (localMatch, template, channelId, restored, restoredFromNode = false) => {
    saveCountRef.current += 1;
    openedOnRef.current = null;
    setLocalSelection({
      key: localMatch.key,
      Panel: localMatch.Panel,
      channelId,
      template,
      restored,
      instanceKey: `${localMatch.key}-${saveCountRef.current}`,
      restoredFromNode,
    });
  };

  // Mounts the shared component on a template with its values. It reports that template again on mount;
  // openedOnRef marks that report as an echo, not a pick (handlePick).
  const showLibraryView = (view) => {
    openedOnRef.current = String(view.defaultTemplate.template.details?.id);
    setRestoreView(view);
  };

  // A template opened on the shared component: what the marketer types there is invisible to us, so what we remember of it
  // is what we opened it with (`prefill`: the carried values, or null for none). A later pick or switch then carries that,
  // not an earlier template's values under that template's name. Opened with none, it is the working draft all the same,
  // blank as it is: the next switch carries nothing rather than values from before a form we cannot see into.
  const noteLibraryOpen = (template, prefill) => {
    if (!tracksValues(template)) return;
    const entry = {
      ...prefill,
      channelId: templatesLoadedForChannel,
      templateId: String(template.details?.id),
      messageType: messageTypeForTemplate(template) || 'text',
      templateName: template.details?.name,
    };
    remember(entry);
    if (!prefill) lastValuedKeyRef.current = memoryKey(entry.channelId, entry.templateId);
  };

  // Opens `template` as a new instance, so whatever the old one held is dropped. `prefill` is the values carried
  // from the saved message, or null to open bare; `carryInfo` feeds the notice. The one place that picks between a
  // local panel and the shared component.
  const openTemplate = (template, prefill, carryInfo = null) => {
    const localMatch = resolvePanel(template);
    if (!localMatch && !activeChannelObj) return;
    setCarry(carryInfo);
    // A remount on the same selectedTemplateInfo does not re-run the double-brace watcher, so clear its notice here.
    setHasDoubleBrace(false);
    // The watcher only sees what is typed or pasted, and nobody typed the values carried in: they are checked here.
    braceOnOpenRef.current = !!prefill && draftHasDoubleBrace(prefill);
    if (localMatch) {
      showLocalPanel(localMatch, template, openChannelId, prefill);
      return;
    }
    // A panel can be on screen (the switcher goes from one template to another); it has to give way to the shared component.
    setLocalSelection(null);
    noteLibraryOpen(template, prefill);
    saveCountRef.current += 1;
    const key = `open-${saveCountRef.current}`;
    // Bare means no `variables`. The shared component reads any truthy list, even [], as a restore
    // (TemplateMessaging.tsx:129): its Back then deletes the list from our object, filters the templates to this
    // one's type and leaves Categories stuck on it (:509-521). With none it opens on its own blank {{n}} slots, and
    // Back shows the full list.
    showLibraryView(prefill
      ? toRestoreView(prefill, template, activeChannelObj, key, true)
      : { defaultChannel: activeChannelObj, defaultTemplate: { template }, key, fromSave: true });
  };

  // templateList is explicit (not the closed-over state) to avoid a stale-closure read.
  const computeState = (draftValue, templateList) => {
    const channel = channels.find((c) => c.value === draftValue.channelId);
    const template = findTemplate(draftValue.templateId, templateList);
    // Always template-derived once the template resolves — mirrors messageType below, and the
    // component's own submitted isTemplate can't be trusted. Only the caller's draftValue is used
    // when the template itself can't be found (e.g. a stale restore). The derived value is what the draft and
    // widget_state keep, so a rebuild that cannot find the template again keeps it too.
    const isTemplate = template ? isTypeTemplate(template.details) : draftValue.isTemplate === true;
    // widget_state doesn't persist messageType, so a restore would otherwise fall back to 'text'
    // below and send a media template as SENDMESSAGE — the template's own type wins instead.
    const messageType = messageTypeForTemplate(template) || draftValue.messageType || 'text';
    const ctx = {
      orgId,
      phoneAttribute,
      // Credentials are keyed by the number's digits only (the setup route and ops write auth.bloomreach.<digits>),
      // however the channel stores the number.
      projectId: org?.auth?.bloomreach?.[digitsOnly(channel?.whatsappNo)]?.projectToken || orgId,
      // The message's own. An emptied field is general consent on purpose, so no fallback to the org's here.
      consentCategory: String(draftValue.consentCategory ?? '').trim() || null,
      launchParams,
    };
    const webhook = buildDefinition({ ...draftValue, isTemplate, messageType }, ctx);
    const state = toWidgetState({ ...draftValue, isTemplate, messageType }, phoneAttribute);
    const tooLarge = byteLength(state) > MAX_BYTES;
    return { draft: { ...draftValue, isTemplate, messageType }, webhook, widget_state: tooLarge ? null : state, tooLarge };
  };

  // The restore finishes after an await, and the org can reload meanwhile (Bloomreach access set up from the consent field adds
  // the number's project token). It builds with what stands when the list lands, not with the render it started in: the
  // org-reload effect below ran before there was a draft, so nothing rebuilds a draft the restore builds late.
  const latestRef = useRef(null);
  latestRef.current = { channels, computeState };

  // What the shell asks on Done: 'never_saved' (a template is open and nothing was saved), 'unsaved_changes' (a saved message,
  // and something on screen that it lacks) or 'none'. Set as the page draws, from the same facts as the footer's status.
  const unsavedRef = useRef('none');

  useImperativeHandle(ref, () => ({
    getState: () => (computedState ? { webhook: computedState.webhook, widget_state: computedState.widget_state } : null),
    getUnsaved: () => unsavedRef.current,
  }));

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Restores an edited node from widget_state; best-effort, so Done still works either way.
  useEffect(() => {
    if (restoreAttemptedRef.current || !incomingWidgetState || channels.length === 0) return;
    const restored = fromWidgetState(incomingWidgetState);
    if (!restored || !restored.channelId) return;
    restoreAttemptedRef.current = true;
    // A Save, or the tab going away, while the list loads makes this restore stale: it then writes nothing, and the
    // state it would push is not the latest one any more.
    const savesAtStart = savesRef.current;
    const isStale = () => !mountedRef.current || savesRef.current !== savesAtStart;

    (async () => {
      try {
        // Restored before the template check below: the destination and consent category belong to the
        // message, so they must survive even when the saved template is gone and the marketer picks another.
        const restoredDestination = normalizeDestination(restored.destination, phoneAttribute);
        setDestination(restoredDestination);
        const restoredConsent = startingConsent(restored.consentCategory, org);
        setConsentCategory(restoredConsent);

        // Through fetchChannelList, not the shared component's ask: this list is the saved channel's whatever ask is newest.
        const loaded = await fetchChannelList(restored.channelId);
        if (isStale()) return;
        const list = loaded.mapped;
        const { channels: latestChannels, computeState: build } = latestRef.current;
        const channel = latestChannels.find((c) => c.value === restored.channelId);
        // The saved channel's list goes on screen, unless the marketer has asked for another channel meanwhile (a click in the
        // Channels filter): the filter and the component's rows are on that one, and the saved list under it would leave the
        // host on a channel they are not. The shared component's own ask as it mounts is for the saved channel, so a reopen
        // with nobody clicking lands here. The saved list then becomes the newest channel asked for, so a reply still in flight
        // cannot undo it. A pick made meanwhile stays on the list it was made on, and a channel the org no longer has is left
        // as it was. The saved form below opens on its own channel either way.
        const asked = newestChannelRef.current;
        if (channel && !pickedRef.current && (asked === null || asked === restored.channelId)) {
          newestChannelRef.current = restored.channelId;
          applyList(restored.channelId, loaded);
        }
        const template = findTemplate(restored.templateId, list);

        if (!template) {
          // The saved template no longer exists (e.g. deleted from the list in this same
          // session, or — for a carousel — its cards can no longer be loaded). Don't compute/push
          // a degraded, name-less draft just because this tab mounted — that would silently
          // overwrite the last known-good webhook/widget_state (held in computedState here and
          // mirrored into Widget's latestState/incomingRef via onStateChange) the moment the user
          // clicks Done without noticing anything is wrong. Leave computedState exactly as it was
          // seeded and only warn.
          setToast({
            open: true,
            type: 'warning',
            message: 'The template used by your saved message has been deleted. Pick a new template and save again.',
          });
          return;
        }

        // restored.isTemplate (from widget_state) is not the final word here either — computeState
        // recomputes isTemplate from this same resolved template, so a stale stored value can't override it.
        const seededDraft = {
          ...restored, destination: restoredDestination, consentCategory: restoredConsent,
          messageType: undefined, templateName: template.details?.name,
        };
        const computed = build(seededDraft, list);
        setComputedState(computed);
        // A new saved message: what a panel reported of an earlier one no longer stands for it.
        savedReportRef.current = null;
        // Not over what a pick made meanwhile: that template's values are the ones in front of the marketer.
        if (!pickedRef.current) remember(computed.draft);
        // Restored, not changed: the shell keeps handing Bloomreach the saved node until the marketer changes it.
        onStateChange?.({ webhook: computed.webhook, widget_state: computed.widget_state }, { restored: true });

        // The template's variable counts or dynamic-button field may have changed since this was
        // saved — the restored headerParams/params/buttonUrlParam/ctaButtonUrl would then land in
        // the wrong slots. Still restore (Bloomreach already holds the old webhook either way);
        // just warn so the marketer reviews and re-saves.
        if (hasTemplateDrifted(template, restored)) {
          setToast({ open: true, type: 'warning', message: ERROR_COPY.templateChangedSinceSave });
        }

        // The marketer picked a template while this was loading. The saved message stays saved; they are not
        // pulled back to it.
        if (pickedRef.current) return;

        if (!channel) return;

        const localMatch = resolvePanel(template);
        if (localMatch) {
          setSelectedTemplateInfo(template);
          // The draft is the panel's restored shape: each panel reads only the keys it has fields for.
          showLocalPanel(localMatch, template, restored.channelId, restored, true);
          return;
        }

        showLibraryView(toRestoreView(restored, template, channel, `restored-${restored.channelId}-${restored.templateId}`));
      } catch {
        // Best-effort: computedState stays as it was seeded, so Done still returns the saved state. The form on screen is
        // not the saved message, though, and the marketer is told.
        if (!isStale()) setToast({ open: true, type: 'warning', message: ERROR_COPY.restoreFailed });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingWidgetState, channels.length]);

  // Watch on the composer's fields: the shared component refuses Save on a literal {{ }}, and Bloomreach's own
  // personalization panel copies {{ customer.first_name }}. A paste converts to single braces at once
  // (webhook/doubleBraces.js). Typing converts when the field is left, not per key: a marketer who types the braces
  // first holds a whole {{c}} after one letter. The notice only flags what is still unconvertible after that, like an
  // unclosed {{, and stays hidden while someone types.
  // It listens on the composer wrapper, which holds the shared component and our panel and nothing else: Send to, Consent
  // category and the template switcher sit outside it. The shared component is skipped while a panel hides it.
  useEffect(() => {
    setHasDoubleBrace(false);
    const wrapper = composerRef.current;
    if (!wrapper) return undefined;
    const isComposerField = (field) => field.matches?.('input, textarea') && !field.closest('[hidden]');
    const scan = () => {
      const fields = Array.from(wrapper.querySelectorAll('input, textarea')).filter(isComposerField);
      setHasDoubleBrace(fields.some((field) => String(field.value || '').includes('{{')));
    };
    const convert = (field) => {
      rewriteFieldDoubleBraces(field);
      scan();
    };
    // Typing and deleting leave the text alone, and so does our own re-dispatched event (it has no inputType).
    const onInput = (event) => {
      if (!isComposerField(event.target)) return;
      if (event.inputType === 'insertFromPaste' || event.inputType === 'insertFromDrop') convert(event.target);
      else setHasDoubleBrace(false);
    };
    // Clicking the shared component's Save moves focus first, so this runs before its click handler reads the value.
    const onFocusOut = (event) => {
      if (isComposerField(event.target)) convert(event.target);
    };
    wrapper.addEventListener('input', onInput, true);
    wrapper.addEventListener('focusout', onFocusOut, true);
    return () => {
      wrapper.removeEventListener('input', onInput, true);
      wrapper.removeEventListener('focusout', onFocusOut, true);
    };
  }, [channels.length, selectedTemplateInfo]);

  // A form opened with carried values that still hold a double brace starts with the notice up; the watch above resets it
  // for the new template and only flags what is typed from then on. It is declared after the watch on purpose: effects run
  // in order, so the notice raised here comes last. A new instance of the form is a new key.
  const formKey = localSelection ? localSelection.instanceKey : restoreView?.key;
  useEffect(() => {
    if (braceOnOpenRef.current) setHasDoubleBrace(true);
    braceOnOpenRef.current = false;
  }, [formKey]);

  // Shared by the library's own submit (via callbackMethod's 'submit' branch below) and a local
  // panel's onSubmit (via handleLocalSubmit) — both hand this the same library-shaped `data`
  // object (Footer/index.tsx's own submit handler builds it for the library path; a local panel's
  // buildSubmitData call builds the matching fields — see src/templateSend/shared/submitData.js).
  // Returns the saved draft.
  const saveDraft = (data) => {
    savesRef.current += 1;
    // The template is found in the list of the channel it is saved on, not in whichever list is on screen (another channel's can
    // be, after a click in the Channels filter). A template that list lacks would keep the library's raw isTemplate, which
    // says true for a footer-only or header-only template, and Enterprise rejects every send of it.
    const list = listOf(data, templates);
    const template = findTemplate(data.templateId, list);
    const draft = draftFromSubmitData(data, { destination, consentCategory, templateName: template?.details?.name });
    const computed = computeState(draft, list);
    setComputedState(computed);
    // What was just saved is what the marketer has now.
    remember(computed.draft);
    // The stored draft is the payload just saved, in a panel's own format, so it is the reference now.
    savedReportRef.current = null;
    lastReportRef.current = null;
    setValuesDirty(false);
    onStateChange?.({ webhook: computed.webhook, widget_state: computed.widget_state });
    // Once saved, the values are the marketer's own, so the carry-over notice goes.
    setCarry(null);
    setToast(
      computed.tooLarge
        ? { open: true, type: 'warning', message: 'Restore state too large, Bloomreach will not remember your selections' }
        : draft.destination.type === 'number'
        ? { open: true, type: 'warning', message: `Saved. Every send from this message goes to ${draft.destination.value} until you reset it.` }
        : { open: true, type: 'success', message: 'Saved. Test webhook and Done now use this message.' }
    );
    return draft;
  };

  // "Restored from the saved node" ends with the first Save, and with Dismiss: both flip the flag the banner reads, on
  // the panel selection and on the restore view.
  const endRestoreBanner = () => {
    setLocalSelection((current) => current && { ...current, restoredFromNode: false });
    setRestoreView((current) => current && { ...current, fromSave: true });
  };

  // A local panel's onSubmit. The panel stays open with its values after Save, so the marketer can
  // go straight to Test webhook; the library path does the same (see callbackMethod's 'submit').
  const handleLocalSubmit = (data) => {
    saveDraft(data);
    endRestoreBanner();
    return true;
  };

  // After an org reload (e.g. Bloomreach access just set up) the saved webhook picks up the number's projectToken
  // without a re-save. The ref holds the org last seen, so the first render pushes nothing.
  const lastOrgRef = useRef(org);
  useEffect(() => {
    if (lastOrgRef.current === org) return;
    lastOrgRef.current = org;
    const draft = computedState?.draft;
    // A draft whose channel the new org no longer has would rebuild with the org id as its project id.
    if (!draft || !channels.some((channel) => channel.value === draft.channelId)) return;
    const computed = computeState(draft, listOf(draft));
    setComputedState(computed);
    onStateChange?.({ webhook: computed.webhook, widget_state: computed.widget_state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  // The shared component has no way to be told "show the list", so a click on its own Back button does it.
  // Follow-up upstream: a showList() ref, or a controlled selectedTemplate. Returns whether there was a button to click:
  // it exists only while the component sits on its form for the template picked from its list.
  const returnLibraryToList = () => {
    const back = composerRef.current?.querySelector(LIBRARY_BACK_BUTTON);
    back?.click();
    return !!back;
  };

  // Back from a local panel. The shared component stays mounted behind it, so its own Back goes first when it can: its
  // list comes back as the marketer left it (search, category and channel kept, no reload). Otherwise a fresh instance
  // loads the list: after a restore the component never left its list, and a restore view (left set) would remount it on
  // the OLD saved template.
  const handleLocalBack = () => {
    if (restoreView || !returnLibraryToList()) {
      setRestoreView(null);
      setLibraryEpoch((epoch) => epoch + 1);
    }
    setLocalSelection(null);
    setSelectedTemplateInfo(null);
    setCarry(null);
    openedOnRef.current = null;
  };

  // Start blank and Reset take away the button the marketer just pressed, and a focused element that leaves the page drops
  // focus to <body>. The root takes it first. It is the root and not .br-composer: that wrapper is display: contents
  // (index.css), which has no box, and Chrome does not focus an element without one.
  const keepFocus = () => messageRef.current?.focus({ preventScroll: true });

  // "Start blank" on the carry-over notice: the same template, nothing carried. The template's own entry becomes a mark that
  // it was left blank on purpose (choosePrefill reads it), so a later visit does not carry again, whichever template was the
  // source of the values. The working draft is the entry lastValuedKeyRef names; replacing that entry forgets what was carried
  // in, so the next switch does not bring it back either.
  const handleStartBlank = () => {
    if (!selectedTemplateInfo) return;
    const templateId = String(selectedTemplateInfo.details?.id);
    const key = memoryKey(openChannelId, templateId);
    sessionRef.current.set(key, { channelId: openChannelId, templateId, startedBlank: true });
    if (lastValuedKeyRef.current === key) lastValuedKeyRef.current = null;
    openTemplate(selectedTemplateInfo, null);
    keepFocus();
  };

  // "Reset unsaved values" on the template list: forgets everything typed this session, so the next pick carries only from the
  // saved message (choosePrefill's `saved`). The saved draft, the webhook and the message's own fields stay as they are, and
  // so does the saved template's own entry: a template the shared component draws reopens with its saved values only through
  // it. The toast's state change redraws the list, which drops the notice (the store is a ref).
  const handleResetTyped = () => {
    sessionRef.current.clear();
    lastValuedKeyRef.current = null;
    if (computedState?.draft) remember(computedState.draft);
    keepFocus();
    setToast({ open: true, type: 'success', message: 'Unsaved values cleared.' });
  };

  // Only the saved template's own values can differ from the saved message: another template already reads as unsaved.
  const isSavedTemplate = (draft) => {
    const saved = computedState?.draft;
    return !!saved && saved.channelId === draft.channelId && sameId(saved.templateId, draft.templateId);
  };
  const differsFromSaved = (draft) => isSavedTemplate(draft) && !sameValues(savedReportRef.current ?? computedState.draft, draft);

  // A local panel reports its values on mount and on every change, in the shape its Save submits and built without
  // validation. They are remembered, and compared with the saved message for the save status. Nothing on screen reads
  // them otherwise, so this re-renders nothing unless the status flips.
  const handleValuesChange = (data) => {
    const draft = draftFromSubmitData(data, { templateName: findTemplate(data.templateId)?.details?.name });
    remember(draft);
    const saved = computedState?.draft;
    const { instanceKey, restored, template } = localSelection;
    // A panel's first report, when it was opened with the saved values, is the saved message as a panel reports it. A
    // template that drifted since the Save is no such panel: its form is not what was saved, and the status must say so.
    if (isSavedTemplate(draft) && instanceKey !== reportedInstanceRef.current && !savedReportRef.current
      && restored && sameValues(restored, saved) && !hasTemplateDrifted(template, restored)) {
      savedReportRef.current = draft;
    }
    reportedInstanceRef.current = instanceKey;
    lastReportRef.current = { instanceKey, draft };
    setValuesDirty(differsFromSaved(draft));
  };

  // The saved message changed (a restore that landed after the marketer picked its template, say): what the open panel last
  // reported is measured against the new one, since it reports nothing more until something is typed.
  useEffect(() => {
    const last = lastReportRef.current;
    if (last && last.instanceKey === localSelection?.instanceKey) setValuesDirty(differsFromSaved(last.draft));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedState]);

  // A media upload. A failure is reported and resolves to null: neither the shared component's drop zone nor our panels
  // catch a rejection.
  const uploadFile = async (file) => {
    try {
      return await uploadMedia(file);
    } catch {
      onError?.(ERROR_COPY.mediaUploadFailed);
      return null;
    }
  };

  // The same upload as a plain URL for a panel. A reply that carries no URL is reported too, or the field stays silently empty.
  const handleUpload = async (file) => {
    const reply = await uploadFile(file);
    if (reply?.url) return reply.url;
    if (reply !== null) onError?.(ERROR_COPY.mediaUploadFailed);
    return null;
  };

  // A template was picked, from the list (the shared component reports it) or in the switcher (`fromSwitcher`: a form of
  // ours may be on screen, so even a pick with nothing to carry has to open one). choosePrefill says what it opens with.
  // Returns false, having done nothing, for a template the form's channel does not list: the form would pair that
  // template with this channel, as a row another channel's reply left in the shared component does.
  const handlePick = (template, fromSwitcher = false) => {
    const id = template.details?.id;
    // A view we opened on this template reports it again when it mounts; that is not a new pick. Nor is the shared
    // component, hidden behind a panel, reporting the template that panel is on.
    if (openedOnRef.current === String(id) || (localSelection && sameId(localSelection.template.details?.id, id))) return true;
    if (!loadedListsRef.current.get(openChannelId)?.mapped.some((listed) => sameId(listed.details.id, id))) return false;
    pickedRef.current = true;
    // The banner is about the saved node's form; a template picked now is not that form, even when it opens nothing new.
    endRestoreBanner();

    const channelId = openChannelId;
    const isPanel = !!resolvePanel(template);
    // A template the shared component draws takes its own earlier values from `own` alone. Its working and saved drafts
    // count only as another template's: a blank one must come back bare, not as an empty restore (see openTemplate), and
    // with the flag off it never reopens from its own save.
    const notItself = (draft) => (!isPanel && draft?.channelId === channelId && sameId(draft.templateId, id) ? null : draft);
    const working = notItself(sessionRef.current.get(lastValuedKeyRef.current));
    const saved = notItself(computedState?.draft);
    const source = working ?? saved;
    // Start blank's mark counts for every template, tracked or not: it is the marketer's word, not a guess at the values.
    const own = sessionRef.current.get(memoryKey(channelId, id));
    let chosen = { prefill: null, carry: null };
    try {
      chosen = choosePrefill({
        own: tracksValues(template) || own?.startedBlank ? own : null,
        working,
        saved,
        toTemplate: template,
        // The template the draft was saved on, as listed now (undefined once it is gone).
        fromTemplate: source ? findTemplate(source.templateId) : undefined,
        channelId,
      });
    } catch {
      // A planner fault must not stop the pick: it opens as a fresh pick, which carries nothing.
    }
    const { prefill, carry: carryInfo } = chosen;
    // The shared component keeps the previous button value across Back (TemplateMessaging.tsx:499-508). A
    // tracked link left in a suffix field passes its check and saves a broken link, so a template with a
    // dynamic button always opens as a new instance, which holds no stale value. Any other template for the
    // shared component needs nothing: its own form is already up, with nothing in it.
    if (prefill || fromSwitcher || isPanel || DYNAMIC_BUTTON_FIELDS[template.details?.button_type]) {
      openTemplate(template, prefill, carryInfo);
      return true;
    }
    noteLibraryOpen(template, null);
    return true;
  };

  // The switcher in the context bar: the same pick as from the list, for a template of this channel, with no list in
  // between. The same template does nothing.
  const handleSwitch = (templateId) => {
    const template = findTemplate(templateId);
    if (!template || sameId(template.details?.id, selectedTemplateInfo?.details?.id)) return;
    if (handlePick(template, true)) setSelectedTemplateInfo(template);
  };

  const callbackMethod = async (action, data) => {
    if (action === 'channelChange') {
      // The library awaits this with no catch of its own, then .map()s the result — a rejection
      // here would both go unhandled and crash the library on the next line. Resolve to [] instead.
      try {
        return await loadTemplatesForChannel(data);
      } catch {
        onError?.(ERROR_COPY.templatesLoadFailed);
        return [];
      }
    }
    if (action === 'upload') return uploadFile(data);
    // Reads the builder's selectedTemplate callback (was ignored) to derive save-status, and to
    // swap in a local panel the moment a template it matches is picked. null is Back.
    if (action === 'selectedTemplate') {
      if (data) {
        if (handlePick(data)) setSelectedTemplateInfo(data);
      } else {
        setSelectedTemplateInfo(null);
        // Back ends the carry-over notice, and the echo marker too, so the same template picked again is a pick.
        openedOnRef.current = null;
        setCarry(null);
        // null is a Back click; a plain view that mounts without a template reports undefined.
        if (data === null) {
          // The restore banner is about the saved node's form, which Back leaves.
          endRestoreBanner();
          // A view we opened with values (a Save reopen, a carry-over, a restore) is the shared component's restore view,
          // whose own Back keeps only templates of that one's type and leaves Categories on it (TemplateMessaging.tsx:509-521).
          // The list comes back whole instead: a fresh instance on the same channel (activeChannelObj).
          if (restoreView) {
            setRestoreView(null);
            setLibraryEpoch((epoch) => epoch + 1);
          }
        }
      }
      return undefined;
    }
    if (action === 'submit') {
      // A template its channel does not list is a row of another channel's list under this one's name, and saving the pair would
      // send every message from a number that does not own the template. The shared component has already dropped back to its
      // list (resetLiveChat runs before this callback, TemplateMessaging.tsx:275-287) and takes only a true reply as saved, so
      // false leaves it there with nothing saved.
      if (!loadedListsRef.current.get(data.channelId)?.mapped.some((listed) => sameId(listed.details.id, data.templateId))) {
        setToast({ open: true, type: 'error', message: ERROR_COPY.templateNotOnNumber });
        return false;
      }
      const saved = saveDraft(data);
      // The shared component drops back to its list on Save (resetLiveChat runs before this callback,
      // TemplateMessaging.tsx:275-287). Reopen the same template with the values just saved, so the
      // marketer stays on the form and can go straight to Test webhook.
      const template = findTemplate(saved.templateId, listOf(saved, templates));
      const channel = channels.find((c) => c.value === saved.channelId);
      if (template && channel) {
        saveCountRef.current += 1;
        showLibraryView(toRestoreView(saved, template, channel, `saved-${saveCountRef.current}`, true));
      }
      return true;
    }
    // 'senderChange' is intentionally ignored here.
    return undefined;
  };

  // Stable, so the shared component can stay memoized behind a panel (WhileHidden) and the switcher keeps its props.
  const libraryCallback = useStableCallback(callbackMethod);
  const onSwitch = useStableCallback(handleSwitch);

  if (channels.length === 0) {
    // The shell's onboarding banner already covers this state.
    unsavedRef.current = 'none';
    return null;
  }

  const phoneNumbers = [{ label: `customer['${phoneAttribute}'] (Bloomreach attribute)`, value: phoneAttribute }];
  const config = { appName: APP_NAME, orgId };
  const templatesReady = templatesLoadedForChannel !== null;

  // What the last save (or restore) holds. Those leave a draft in computedState; until one does,
  // computedState is initialState (Widget's latestState: { webhook, widget_state }, no draft), so
  // fall back to the destination in its widget_state. That keeps the list-view notice and the save
  // status right through the restore window after a tab remount.
  const savedDestination = normalizeDestination(
    computedState?.draft?.destination ?? computedState?.widget_state?.destination,
    phoneAttribute
  );
  const savedToTestNumber = savedDestination.type === 'number';

  // Unsaved: the open template is not the saved one, or the destination or consent category differs (Save commits
  // everything, so a change of either waits for the next Save), or the form shows values carried from another template (the
  // notice), or the open panel holds values the saved draft does not. The shared component's own form reports no values, so
  // the notice is all it has to go by. Also requires selectedTemplateInfo, else a restore's 1-render gap flashes "Unsaved".
  // draft is read optionally: with a draft-less computedState (see above), picking a template reads as unsaved instead of
  // throwing.
  const isDraftDirty = !!(
    computedState &&
    selectedTemplateInfo &&
    (!sameId(selectedTemplateInfo.details?.id, computedState.draft?.templateId) ||
      openChannelId !== computedState.draft?.channelId ||
      !isSameDestination(destination, savedDestination) ||
      consentCategory !== (computedState.draft?.consentCategory ?? '') ||
      !!carry ||
      (!!localSelection && valuesDirty))
  );
  // A template is open and nothing was ever saved, so Done has no message to hand Bloomreach.
  const neverSaved = !computedState && !!selectedTemplateInfo;
  unsavedRef.current = neverSaved ? 'never_saved' : isDraftDirty ? 'unsaved_changes' : 'none';
  const saveStatus = neverSaved
    ? { tone: 'unsaved', text: 'Not saved yet. Click Save before Done.' }
    : !computedState
    ? null
    : isDraftDirty
    ? { tone: 'unsaved', text: 'Unsaved changes. Click Save before Done.' }
    : { tone: 'saved', text: savedToTestNumber ? 'Saved with a test number · Test webhook or Done' : 'Saved · Test webhook or Done' };

  // footerComponent shows the save status once there is one; with no template open, the generic helper text. Read by
  // both TemplateMessaging's own Footer and a local panel's SendPanelShell.
  const customisations = {
    ...CUSTOMISATIONS_BASE,
    footerComponent: (
      <span className="br-footer-left">
        {saveStatus ? (
          <span data-testid="bloomreach-message-save-status" className={`br-save-status br-save-status--${saveStatus.tone}`}>
            {saveStatus.text}
          </span>
        ) : (
          <span className="br-save-status br-save-status--hint">{FOOTER_HELPER_TEXT}</span>
        )}
        <span className="br-footer-caption" data-testid="bloomreach-widget-header-note">
          Test webhook sends a real WhatsApp message.
        </span>
      </span>
    ),
  };

  // Uses raw .channelName, not .label (which item 15 now suffixes with "· whatsappNo").
  const selectedChannelName = openChannelObj?.channelName;
  // The number the consent dropdown asks about: the sending channel's, found the way computeState finds a saved
  // message's (channels.find by id, then whatsappNo). The form on screen is on that channel for a fresh pick, a
  // restore and a switch alike (openChannelId). Digits only, as gs-intg's consent-categories route takes it.
  // Undefined until a channel's templates have loaded.
  const sendingNumber = digitsOnly(openChannelObj?.whatsappNo) || undefined;

  const panel = localSelection && (
    <localSelection.Panel
      key={localSelection.instanceKey}
      template={localSelection.template}
      channelId={localSelection.channelId}
      orgId={orgId}
      onSubmit={handleLocalSubmit}
      onBack={handleLocalBack}
      restored={localSelection.restored}
      customisations={customisations}
      onValuesChange={handleValuesChange}
      onUpload={handleUpload}
    />
  );

  // The panel shows the footer, so the hidden shared component behind it gets none (its form would draw a second copy).
  // Only a prop changes, which the component does not remount on.
  const libraryCustomisations = panel ? CUSTOMISATIONS_BASE : customisations;
  const plainTemplateMessaging = (
    <TemplateMessaging
      key={restoreView ? `${restoreView.key}-fallback` : `default-${libraryEpoch}`}
      channels={channels}
      phoneNumbers={phoneNumbers}
      config={config}
      callbackMethod={libraryCallback}
      defaultChannel={activeChannelObj ?? savedChannelObj}
      customisations={libraryCustomisations}
      // Empty list keeps the library's carousel Select from crashing on the frame before a local
      // panel swaps in; every other use is unaffected — CustomInput already defaults variables to
      // [], and the copy-code fallback field only shows when variables.length > 0.
      variables={[]}
    />
  );
  const library = restoreView ? (
    <TemplateMessagingBoundary key={`${restoreView.key}-boundary`} fallback={plainTemplateMessaging}>
      <TemplateMessaging
        key={restoreView.key}
        channels={channels}
        phoneNumbers={phoneNumbers}
        config={config}
        callbackMethod={libraryCallback}
        defaultChannel={restoreView.defaultChannel}
        defaultTemplate={restoreView.defaultTemplate}
        customisations={libraryCustomisations}
        variables={[]}
      />
    </TemplateMessagingBoundary>
  ) : (
    plainTemplateMessaging
  );

  // restoreView is only ever set with a resolved template — safe to gate the banner on it.
  // A carry-over or bare view is opened fromSave, so it never shows the "Restored" banner. A local panel on
  // screen has replaced that view, even though restoreView stays set until Back; it shows the banner itself while it is
  // the panel a restore opened (until its first Save or a Dismiss).
  const showRestoreBanner = localSelection ? !!localSelection.restoredFromNode : !!restoreView && !restoreView.fromSave;
  const hasVariables = selectedTemplateInfo
    ? /\{\{\d+\}\}/.test(`${selectedTemplateInfo.details?.header || ''} ${selectedTemplateInfo.details?.body || ''}`)
    : null;

  // The template name is a dropdown with the own-form flag on, over this channel's templates. It keeps the plain name when
  // the list on screen is not the open form's channel (the dropdown would offer another channel's templates), and when the
  // list does not hold the open template (it cannot be shown in the dropdown).
  const showSwitcher = SHOW_SWITCHER && !!selectedTemplateInfo
    && templatesLoadedForChannel === openChannelId
    && templates.some((template) => sameId(template.details?.id, selectedTemplateInfo.details?.id));

  const templateChips = selectedTemplateInfo
    ? [selectedTemplateInfo.details?.category, selectedTemplateInfo.details?.language, selectedTemplateInfo.details?.type]
        .filter(Boolean)
        .map((chip) => String(chip).toUpperCase())
    : [];

  // --br-template-count fills the table header's own empty action slot (see overrides.css).
  // templates.length already includes carousels with loadable cards (see buildChannelList).
  const messageStyle = templates.length
    ? { '--br-template-count': JSON.stringify(`${templates.length} template${templates.length === 1 ? '' : 's'}`) }
    : undefined;

  const hiddenNote = hiddenNoteText(hiddenKinds);

  // How many templates hold a value the saved message does not, on the list page only (no template and no panel open). An
  // entry equal to the saved draft is the saved message itself, and so is the first report of a panel opened on it (a node
  // the shared component saved holds its JSON in another format, see savedReportRef). The store is a ref, so this is read
  // per render: coming back to the list (Back) redraws the host, and so does a reset.
  const savedDraft = computedState?.draft;
  const isSavedValues = (entry) => (!!savedDraft && sameValues(entry, savedDraft))
    || (!!savedReportRef.current && sameValues(entry, savedReportRef.current));
  const typedCount = SHOW_RESET_TYPED && !selectedTemplateInfo && !localSelection
    ? Array.from(sessionRef.current.values()).filter((entry) => draftHasValue(entry) && !isSavedValues(entry)).length
    : 0;

  return (
    <ThemeProvider config={GUPSHUP_THEME_CONFIG}>
      <div className={`br-message${hasVariables === false ? ' br-no-vars' : ''}`} style={messageStyle} ref={messageRef} tabIndex={-1}>
        {templatesReady && templates.length === 0 && (
          <p data-testid="bloomreach-message-no-templates">{ERROR_COPY.emptyTemplates}</p>
        )}
        {hiddenNote && (
          <p data-testid="bloomreach-message-hidden-note">{hiddenNote}</p>
        )}
        {!selectedTemplateInfo && savedToTestNumber && (
          <p className="br-notice br-notice--warn" data-testid="bloomreach-message-test-number-notice">
            This message is saved with test number {savedDestination.value}. Every send goes there, not to customers. Before going live, open the template, click Reset next to Send to, click Save, then click Done.
          </p>
        )}
        {typedCount > 0 && (
          <p className="br-notice br-notice--info" data-testid="bloomreach-message-typed-notice">
            Unsaved values are kept for {typedCount} template{typedCount === 1 ? '' : 's'}.
            <button type="button" className="br-notice__action" data-testid="bloomreach-message-reset-typed" onClick={handleResetTyped}>
              Reset unsaved values
            </button>
          </p>
        )}

        {/* selectedTemplateInfo reflects both paths now — a library pick/restore (via the
            'selectedTemplate' callback) or a local-panel pick/restore (set directly alongside
            localSelection above) — so this bar and the Jinja help/double-brace notice below
            work the same regardless of which form is showing. */}
        {(selectedTemplateInfo || showRestoreBanner) && (
          <div className="br-context-bar" data-testid="bloomreach-message-template-summary">
            {selectedTemplateInfo ? (
              <div className="br-context-bar__left">
                {showSwitcher ? (
                  <TemplateSwitcher templates={templates} value={selectedTemplateInfo.details?.id} onChange={onSwitch} />
                ) : (
                  <span className="br-context-bar__name">{selectedTemplateInfo.details?.name}</span>
                )}
                <span className="br-context-bar__divider" />
                <span className="br-context-bar__chips">
                  {templateChips.map((chip, index) => (
                    <span key={`${chip}-${index}`} className="br-context-bar__chip">
                      {chip}
                    </span>
                  ))}
                </span>
              </div>
            ) : (
              <span className="br-context-bar__meta">
                Editing the saved message: {restoreView.defaultTemplate.template.details?.name}
              </span>
            )}
            <div className="br-context-bar__right">
              {selectedTemplateInfo && selectedChannelName && (
                <span className="br-context-bar__meta">via {selectedChannelName}</span>
              )}
              {showRestoreBanner && (
                <span className="br-context-bar__restore" data-testid="bloomreach-message-restore-banner">
                  Restored from the saved node
                  <button
                    type="button"
                    aria-label="Dismiss"
                    data-testid="bloomreach-message-restore-banner-dismiss"
                    className="br-context-bar__dismiss"
                    onClick={endRestoreBanner}
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          </div>
        )}

        {selectedTemplateInfo && (
          <>
            <SendToControl value={destination} defaultAttribute={phoneAttribute} onChange={setDestination} />
            <ConsentControl
              orgId={orgId}
              number={sendingNumber}
              value={consentCategory}
              onChange={setConsentCategory}
              onCredentialsSaved={reloadOrg}
            />
            <p className="br-hint" data-testid="bloomreach-widget-token-hint">
              Paste personalization the way you write it in Bloomreach, like <code>{'{{ customer.first_name }}'}</code>. It may
              show as <code>{'{customer.first_name}'}</code>, but it works the same.
            </p>
            <details className="br-jinja-help" data-testid="bloomreach-message-jinja-help">
              <summary>Writing expressions</summary>
              <ul className="br-jinja-help__notes">
                <li>
                  Customer attributes and event properties both work, and so do filters, like{' '}
                  <code>{"{{ customer.first_name | default('there') }}"}</code>.
                </li>
                <li>
                  No <code>{'{% if %}'}</code> or <code>{'{% for %}'}</code> blocks. Put conditions in the scenario.
                </li>
                <li>
                  Don't add <code>| json</code>. We add it.
                </li>
                <li>Use Test webhook to check the rendered message.</li>
              </ul>
            </details>
          </>
        )}
        {hasDoubleBrace && selectedTemplateInfo && (
          <p className="br-notice br-notice--warn" data-testid="bloomreach-message-double-brace-notice">
            Close the braces to save, like {'{{ customer.first_name }}'}.
          </p>
        )}
        {/* A live region is read out when its text changes, and often missed when it arrives with its text. This one is in the
            page from the first render, empty, and the carry notice is drawn into it. It never remounts on a switch (index.css
            takes it out of the flow while it is empty). */}
        <div role="status" className="br-live" data-testid="bloomreach-message-carry-status">
          {carry && (
            <p className="br-notice br-notice--info" data-testid="bloomreach-message-carry-notice">
              Kept from {carry.fromName}: {carry.kept.join(', ')}. Check them, then Save.
              <button type="button" className="br-notice__action" data-testid="bloomreach-message-carry-start-blank" onClick={handleStartBlank}>
                Start blank
              </button>
            </p>
          )}
        </div>

        {/* Everything the marketer composes in, so the double-brace watch sees nothing else. With the own-form flag on, the
            shared component stays mounted behind a panel, hidden (index.css), and keeps its list for Back
            (returnLibraryToList). Off, the panel takes its place, as it always did. */}
        <div className="br-composer" ref={composerRef}>
          {(KEEP_LIBRARY || !panel) && (
            <div className="br-library" hidden={!!panel}>
              <WhileHidden hidden={!!panel}>{library}</WhileHidden>
            </div>
          )}
          {panel}
        </div>

        <Toast
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          data-testid="bloomreach-message-toast"
          maxWidth="30vw"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          title={toast.message}
          type={toast.type}
          open={toast.open}
          autoHideDuration={5000}
        />
      </div>
    </ThemeProvider>
  );
});

export default Message;
